import React from 'react'
import { createUseStyles } from 'react-jss'
import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { ChevronDownFill, ChevronRight } from '../../../assets/icons'
import { colors } from '../../../theme/palette'
import { Typography } from '../Typography/Typography'

export interface Props {
    title: string
    variant?: 'default' | 'select'
}

const useStyles = createUseStyles({
    '@keyframes slideDown': {
        from: {
            height: 0,
        },
        to: {
            height: 'var(--radix-accordion-content-height)',
        },
    },
    '@keyframes slideUp': {
        from: {
            height: 'var(--radix-accordion-content-height)',
        },
        to: {
            height: 0,
        },
    },
    accordion: {
        position: 'relative',
        borderRadius: '6px',
        backgroundColor: '#fff',
        border: '1px solid rgba(0, 0, 0, 0.2);',
        padding: '0 1.25rem',
    },
    trigger: {
        position: 'relative',
        all: 'unset',
        fontFamily: 'inherit',
        backgroundColor: 'transparent',
        padding: 0,
        height: '45px',
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: '15px',
        lineHeight: 1,
        color: 'hsl(250 43.0% 48.0%)',
        boxShadow: '0 1px 0 hsl(278deg 4% 89%)',
        width: '100%',
        cursor: 'pointer',
        '& > svg': {
            width: '6px',
            transform: 'rotate(-90deg)',
        },
        '&[data-state="open"] > svg': {
            transform: 'rotate(90deg)',
        },
    },
    close: {
        width: '8px',
        right: '5px',
        position: 'absolute',
        transition: 'all .1s ease-in',
        '[data-state="open"] > &': {
            transform: 'rotate(0deg)',
        },
    },
    item: {
        overflow: 'hidden',
        marginTop: 1,

        '&:first-child': {
            marginTop: 0,
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
        },

        '&:last-child': {
            borderBottomLeftRadius: 4,
            borderBottomRightRadius: 4,
        },

        '&:focus-within': {
            position: 'relative',
            zIndex: 1,
        },
    },
    header: {
        all: 'unset',
        color: colors.green500,
        // margin: '0 1.25rem',
    },
    title: {
        color: colors.green500,
    },
    content: {
        overflow: 'hidden',
        fontSize: 15,
        color: 'black',
        paddingBottom: '1.25rem',
        '&[data-state="open"]': {
            animation: `$slideDown 300ms cubic-bezier(0.87, 0, 0.13, 1)`,
        },
        '&[data-state="closed"]': {
            animation: `$slideUp 300ms cubic-bezier(0.87, 0, 0.13, 1)`,
        },
    },
    formControl: {
        padding: '20px',
    },
    contentWrapper: {
        padding: '20px',
    },
})

const Accordion: React.FC<Props> = ({
    children,
    title,
    variant = 'default',
}) => {
    const classes = useStyles()

    return (
        <AccordionPrimitive.Root type="multiple" className={classes.accordion}>
            <AccordionPrimitive.Item value="item-1" className={classes.item}>
                <AccordionPrimitive.Header className={classes.header}>
                    <AccordionPrimitive.Trigger className={classes.trigger}>
                        <Typography
                            color={
                                variant === 'select'
                                    ? colors.black
                                    : colors.green500
                            }
                            tag="h3"
                            size={variant === 'select' ? 'XS' : 'S'}
                        >
                            {title}
                        </Typography>
                        <ChevronRight className={classes.close} color="#000" />
                    </AccordionPrimitive.Trigger>
                </AccordionPrimitive.Header>
                <AccordionPrimitive.Content className={classes.content}>
                    {children}
                </AccordionPrimitive.Content>
            </AccordionPrimitive.Item>
        </AccordionPrimitive.Root>
    )
}

export { Accordion }
